import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Modal, Form, Card, Row, Col, Typography, Spin, message, Space, Badge, Input, notification } from 'antd';
import { useTranslation } from 'react-i18next';
import network from 'network/network';
import axios from 'axios';
import { LoadingOutlined, DollarOutlined, CheckCircleFilled, UserOutlined, LeftOutlined, CloseCircleFilled } from '@ant-design/icons';
import ReusableButton from 'components/Elements/ReusableButton';
import { API_BASE_URL, API_FULL_URL, ELOGO_APIKEY, ELOGO_SECRETKEY } from 'constants/ApiConstant';
import * as signalR from '@microsoft/signalr';
import { FIRM_NAME, FIRMMAIL } from 'constants/StorageConstant';
import { TfiPackage } from "react-icons/tfi";


const { Title, Text } = Typography;

// Create SignalR connection outside component
let hubConnection = null;

const CampaignPayment = ({ visible, onClose, onSuccess }) => {
    const { t } = useTranslation();
    const [loading, setLoading] = useState(false);
    const [packages, setPackages] = useState([]);
    const [selectedPackage, setSelectedPackage] = useState(null);
    const [showPaymentForm, setShowPaymentForm] = useState(false);
    const [currencyRate, setCurrencyRate] = useState(1);
    const [isLoadingCurrency, setIsLoadingCurrency] = useState(false);
    const [isPaymentSuccess, setIsPaymentSuccess] = useState(false);
    const [isPaymentFailed, setIsPaymentFailed] = useState(false);
    const [isPaymentWaiting, setIsPaymentWaiting] = useState(false);
    const [paymentErrorMessage, setPaymentErrorMessage] = useState('');
    const [form] = Form.useForm();
    const selectedPackageRef = useRef();

    // Add state variables for API credentials
    const apiKey = process.env.REACT_APP_ELOGO_APIKEY;
    const apiSecret = process.env.REACT_APP_ELOGO_SECRETKEY;

    const resetModalState = () => {
        setSelectedPackage(null);
        setShowPaymentForm(false);
        setIsPaymentSuccess(false);
        setIsPaymentFailed(false);
        setIsPaymentWaiting(false);
        setPaymentErrorMessage('');
        form.resetFields();
        selectedPackageRef.current = null;
    };

    const onPaymentCallback = useCallback(async (data) => {
        const selected = selectedPackageRef.current;

        // Reset all payment states


        setIsPaymentSuccess(false);
        setIsPaymentFailed(false);
        setIsPaymentWaiting(false);
        setPaymentErrorMessage('');

        switch (data.payload) {
            case "wait":
                setIsPaymentWaiting(true);
                break;

            case "success":
                if (selected) {
                    try {
                        const creditResponse = await network.post('/admin/Campaign/insertCredit', {
                            credit: selected.credit
                        });

                        if (creditResponse.isSuccess) {
                            setIsPaymentSuccess(true);
                            if (onSuccess) {
                                setTimeout(() => {
                                    onSuccess();
                                }, 100);
                            }
                        } else {
                            setIsPaymentFailed(true);
                            setPaymentErrorMessage(creditResponse.errorMessage || t('errorAddingCredits'));

                        }
                    } catch (creditError) {
                        console.error('Error adding credits:', creditError);
                        setIsPaymentFailed(true);
                        setPaymentErrorMessage(t('errorAddingCredits'));

                    }
                }
                break;

            case "fail":
                // setIsPaymentFailed(true);
                // setPaymentErrorMessage(data.type);

                break;

            default:
                // setIsPaymentFailed(true);
                // setPaymentErrorMessage(data.type);

                break;
        }
    }, [selectedPackage, onSuccess, t]);

    const handleModalClose = () => {
        resetModalState();
        setIsPaymentSuccess(false);
        onClose();
    };

    useEffect(() => {
        if (!hubConnection) {
            hubConnection = new signalR.HubConnectionBuilder()
                .withUrl(`${API_BASE_URL}/paymenthub`)
                .build();

            const startConnection = async () => {
                try {
                    await hubConnection.start();
                    console.log('SignalR Connected');
                } catch (err) {
                    console.error('SignalR Connection Error:', err);
                    setTimeout(() => {
                        if (hubConnection) startConnection();
                    }, 2000);
                }
            };

            hubConnection.onclose(() => {
                console.log('SignalR Connection Closed');
                setTimeout(() => {
                    if (hubConnection) startConnection();
                }, 2000);
            });

            startConnection();
        }


        hubConnection.off('Payment', onPaymentCallback);
        hubConnection.on('Payment', onPaymentCallback);

        return () => {
            hubConnection.off('Payment', onPaymentCallback);
        };
    }, []);


    useEffect(() => {
        return () => {
            if (hubConnection) {
                hubConnection.stop();
                hubConnection = null;
            }
        };
    }, []);

    useEffect(() => {
        selectedPackageRef.current = selectedPackage;
    }, [selectedPackage]);

    useEffect(() => {
        fetchPackages();
        fetchCurrencyRate();
    }, []);

    const fetchCurrencyRate = async () => {
        try {
            setIsLoadingCurrency(true);
            const response = await network.get('/admin/Campaign/currency');

            if (response.isSuccess && response.data) {

                const parser = new DOMParser();
                const xmlDoc = parser.parseFromString(response.data, 'text/xml');


                const usdNode = xmlDoc.querySelector('Currency[Kod="USD"]');
                if (usdNode) {
                    const forexSelling = usdNode.querySelector('ForexBuying');
                    if (forexSelling) {

                        const rate = parseFloat(forexSelling.textContent.replace(',', '.'));
                        if (!isNaN(rate) && rate > 0) {
                            setCurrencyRate(rate);
                            return;
                        }
                    }
                }
                message.error(t('errorParsingRate'));
            } else {
                message.error(t('errorFetchingRate'));
            }
        } catch (error) {
            console.error('Error fetching currency rate:', error);
            message.error(t('errorFetchingRate'));
        } finally {
            setIsLoadingCurrency(false);
        }
    };

    const fetchPackages = async () => {
        try {
            setLoading(true);
            const response = await network.get('/admin/Campaign/GetAllPackages');
            if (response.isSuccess) {
                setPackages(response.data.data || []);
            } else {
                message.error(response.errorMessage || t('failedToFetchPackages'));
            }
        } catch (error) {
            console.error('Error fetching packages:', error);
            message.error(t('errorOccurred'));
        } finally {
            setLoading(false);
        }
    };

    const handlePackageSelect = async () => {
        try {
            setLoading(true);
            const firmName = localStorage.getItem(FIRM_NAME) || '';
            const firmEmail = localStorage.getItem(FIRMMAIL) || '';

            const paymentData = {
                amount: parseInt((parseFloat((selectedPackage.packageprice * currencyRate).toFixed(2)) * 100)).toString(),
                paymentProvider: 0,
                isEditAmount: false,
                isClientSelectPaymentProvider: true,
                paymentDescriptionInputVisible: false,
                paymentDescriptionInputRequired: false,
                paymentDescriptionInitialValue: `Package: ${selectedPackage.packagename}`,
                isSendEmail: false,
                callbackUrl: `${API_FULL_URL}/admin/Payment/callback`,
                isCallbackNotView: true,
                firmLogoUrl: `${API_BASE_URL}/Image/Chatshire%20Logo.png`,
                payButtonTitle: t("pay"),
                customer: {
                    nameSurname: firmName,
                    email: firmEmail,
                    code: '',
                    taxCode: '',
                    gsmNumber: ''
                }
            };

            const credentials = `${apiKey}:${apiSecret}`;
            const base64Credentials = btoa(credentials);

            const response = await axios.post(`https://etahsilatapi.elogo.com.tr/paymentform`, paymentData, {
                headers: {
                    'Content-Type': 'application/json',
                    'Authorization': `Basic ${base64Credentials}`
                }
            });

            if (response.data) {
                if (response.data.iframeUrl) {
                    window.open(response.data.iframeUrl, '_blank', 'noopener,noreferrer');
                } else {
                    message.error(t('invalidPaymentUrl'));
                }
            } else {
                message.error(response.data.errorMessage || t('paymentFailed'));
            }
        } catch (error) {
            console.error('Error processing payment:', error);
            message.error(t('errorOccurred'));
        } finally {
            setLoading(false);
        }
    };

    const renderPackageSelection = () => (
        <>
            <Row gutter={[16, 16]}>
                {packages.map((pkg) => (
                    <Col key={pkg.packageid} xs={24} sm={12} md={8}>
                        <Badge.Ribbon
                            text={selectedPackage?.packageid === pkg.packageid ? t('selected') : null}
                            color="#1890ff"
                            style={{ display: selectedPackage?.packageid === pkg.packageid ? 'block' : 'none' }}
                            ribbonStyle={{ fontSize: '20px', fontWeight: 'bold', padding: '6px 16px' }}
                        >
                            <Card
                                hoverable
                                className={`package-card ${selectedPackage?.packageid === pkg.packageid ? 'selected' : ''}`}
                                onClick={() => setSelectedPackage(pkg)}
                                style={{
                                    borderColor: selectedPackage?.packageid === pkg.packageid ? '#1890ff' : '#d9d9d9',
                                    cursor: 'pointer',
                                    height: '100%',
                                    display: 'flex',
                                    flexDirection: 'column',
                                    position: 'relative'
                                }}
                            >


                                <Title level={4} style={{ marginBottom: '16px' }}>{pkg.packagename}</Title>
                                <Text strong style={{ fontSize: '28px', color: '#1890ff', display: 'block', marginBottom: '8px' }}>
                                    ${pkg.packageprice}
                                </Text>
                                <Text type="secondary" style={{ fontSize: '20px' }}>
                                    ≈ {(pkg.packageprice * currencyRate).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} TL
                                </Text>
                                {isLoadingCurrency && (
                                    <Text type="secondary" style={{ fontSize: '12px' }}>
                                        <LoadingOutlined spin style={{ marginRight: '5px' }} />
                                        {t('updatingRate')}
                                    </Text>
                                )}
                                <div style={{ marginTop: '16px', borderTop: '1px solid #f0f0f0', paddingTop: '16px' }}>
                                    <Text type="secondary" style={{ display: 'block', marginBottom: '8px' }}>
                                        {t('packageDetails')}:
                                    </Text>
                                    <ul style={{
                                        listStyle: 'none',
                                        padding: 0,
                                        margin: 0,
                                        color: '#666'
                                    }}>
                                        <li style={{ marginBottom: '4px' }}>
                                            <CheckCircleFilled style={{ color: '#52c41a', marginRight: '8px' }} />
                                            {t('creditAmount', { amount: pkg.credit.toLocaleString('tr-TR') })}
                                        </li>

                                    </ul>
                                </div>
                            </Card>
                        </Badge.Ribbon>
                    </Col>
                ))}
            </Row>
            {selectedPackage && (
                <Row justify="end" style={{ marginTop: '24px' }}>
                    <Col>
                        <ReusableButton
                            variant="template"
                            text={t('proceedToPayment', { price: selectedPackage.packageprice })}
                            onClick={handlePackageSelect}
                            loading={loading}
                            style={{ minWidth: '250px' }}
                        />
                    </Col>
                </Row>
            )}
        </>
    );

    const renderModalContent = () => {
        if (isPaymentSuccess) {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Title level={5} style={{ marginBottom: '16px', fontSize: "20px" }}>
                        {t('paymentSuccessMessage')}
                    </Title>
                    <Text style={{ fontSize: "16px" }} type="secondary">
                        {t('creditsAddedMessage', { credits: selectedPackage?.credit })}
                    </Text>
                </div>
            );
        }

        if (isPaymentFailed) {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Title level={5} style={{ color: '#ff4d4f', marginBottom: '16px', fontSize: "20px" }}>
                        {t('paymentFailedMessage')}
                    </Title>
                    <Text style={{ fontSize: "16px" }} type="danger">
                        {paymentErrorMessage}
                    </Text>
                </div>
            );
        }

        if (isPaymentWaiting) {
            return (
                <div style={{ textAlign: 'center', padding: '20px 0' }}>
                    <Spin size="large" />
                    <Title level={5} style={{ marginTop: '16px', marginBottom: '16px' }}>
                        {t('paymentProcessingMessage')}
                    </Title>
                    <Text type="secondary">
                        {t('pleaseWaitMessage')}
                    </Text>
                </div>
            );
        }

        return (
            <Spin spinning={loading} indicator={<LoadingOutlined style={{ fontSize: 24 }} spin />}>
                {renderPackageSelection()}
            </Spin>
        );
    };

    return (
        <div style={{ marginTop: "80px" }}>
            <Modal
                centered
                title={
                    <Row justify="space-between" align="middle" style={{ width: '100%' }}>
                        <Col>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                {isPaymentSuccess ? (
                                    <CheckCircleFilled style={{ color: '#52c41a', fontSize: '20px' }} />
                                ) : isPaymentFailed ? (
                                    <CloseCircleFilled style={{ color: '#ff4d4f', fontSize: '20px' }} />
                                ) : isPaymentWaiting ? (
                                    <LoadingOutlined style={{ color: '#1890ff', fontSize: '20px' }} />
                                ) : (
                                    <TfiPackage style={{ fontSize: '20px' }} />
                                )}
                                <span style={{ fontSize: '18px', fontWeight: 400 }}>
                                    {isPaymentSuccess ? t('paymentSuccessTitle') :
                                        isPaymentFailed ? t('paymentFailedTitle') :
                                            isPaymentWaiting ? t('paymentProcessingTitle') :
                                                t('selectPackage')}
                                </span>
                            </div>
                        </Col>
                    </Row>
                }
                open={visible}
                onCancel={handleModalClose}
                footer={isPaymentSuccess || isPaymentFailed ? [
                    <ReusableButton
                        key="close"
                        variant="template"
                        text={t('ok')}
                        onClick={handleModalClose}
                    />
                ] : null}
                width={isPaymentSuccess || isPaymentFailed || isPaymentWaiting ? 450 : 800}
            >

                {renderModalContent()}
            </Modal>
        </div>

    );
};

export default CampaignPayment;
